import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Grid,
  Link,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Chip,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  PersonAddOutlined,
  VerifiedUser,
  Info,
  CheckCircle,
  Error as ErrorIcon,
  Business,
} from '@mui/icons-material';
import { useAuthStore } from '../stores/useAuthStore';
import { CorpsMetier, CORPS_METIER_LABELS } from '../types';
import Logo from '../components/Logo';
import { validatePassword } from '../utils/passwordValidation';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';
import PasswordTextField from '../components/PasswordTextField';
import { validateSiret, normalizeSiret } from '../utils/siretValidation';
import authService, { SiretValidationResponse } from '../services/authService';

const RegisterPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { register, isLoading, isAuthenticated } = useAuthStore();

  // Rediriger vers le dashboard si l'utilisateur est déjà connecté
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    nom: '',
    prenom: '',
    entreprise: '',
    telephone: '',
    adresse: '',
    siret: '',
    siren: '',
    codePaysIso: 'FR',
    numTvaIntracom: '',
    corpsMetier: CorpsMetier.CARRELEUR, // Valeur par défaut
  });

  const [error, setError] = useState('');
  const [siretError, setSiretError] = useState('');
  const [emailError, setEmailError] = useState('');

  // États pour la validation SIRET en temps réel
  const [siretValidating, setSiretValidating] = useState(false);
  const [siretValidated, setSiretValidated] = useState(false);
  const [siretValidationData, setSiretValidationData] = useState<SiretValidationResponse | null>(null);
  const [siretValidationTimer, setSiretValidationTimer] = useState<NodeJS.Timeout | null>(null);

  // Validation email en temps réel
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Fonction de validation SIRET en temps réel via l'API
  const validateSiretWithAPI = useCallback(async (siret: string) => {
    const normalized = normalizeSiret(siret);

    // Vérifier que c'est bien 14 chiffres
    if (normalized.length !== 14) {
      return;
    }

    // Validation Luhn locale d'abord
    const localValidation = validateSiret(siret);
    if (!localValidation.isValid) {
      setSiretError(localValidation.error || 'SIRET invalide');
      setSiretValidated(false);
      return;
    }

    // Appel API pour validation complète
    setSiretValidating(true);
    setSiretError('');

    try {
      const response = await authService.validateSiret(normalized);
      setSiretValidationData(response);

      if (response.valid) {
        setSiretValidated(true);
        setSiretError('');

        // Préremplir le formulaire avec les données INSEE
        setFormData(prev => ({
          ...prev,
          entreprise: response.denominationSociale || prev.entreprise,
          adresse: response.adresse || prev.adresse,
          siren: response.siren || prev.siren,
          // Configuration automatique de la TVA
          numTvaIntracom: response.isExemptFromTVA
            ? '' // Si exonéré, laisser vide
            : (response.numTVAIntracommunautaire || prev.numTvaIntracom),
        }));

        // Afficher un avertissement si la période d'essai est déjà utilisée
        if (response.alreadyUsedTrial && response.trialWarning) {
          setError(response.trialWarning);
        }
      } else {
        setSiretValidated(false);
        setSiretError(response.message || 'SIRET invalide');
      }
    } catch (err: any) {
      setSiretValidated(false);
      setSiretError(err.message || 'Erreur lors de la vérification du SIRET');
    } finally {
      setSiretValidating(false);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
    setError(''); // Réinitialiser l'erreur quand l'utilisateur tape

    // Validation email en temps réel
    if (name === 'email') {
      if (value.trim() === '') {
        setEmailError('');
      } else if (!validateEmail(value)) {
        setEmailError('Format d\'email invalide');
      } else {
        setEmailError('');
      }
    }

    // Validation SIRET en temps réel avec debounce
    if (name === 'siret') {
      // Annuler le timer précédent
      if (siretValidationTimer) {
        clearTimeout(siretValidationTimer);
      }

      // Réinitialiser les états de validation
      setSiretValidated(false);
      setSiretValidationData(null);

      const normalized = normalizeSiret(value);

      if (normalized.length === 0) {
        setSiretError('');
        return;
      }

      if (normalized.length < 14) {
        setSiretError('Le SIRET doit contenir 14 chiffres');
        return;
      }

      // Validation Luhn locale immédiate
      const validation = validateSiret(value);
      if (!validation.isValid) {
        setSiretError(validation.error || '');
        return;
      }

      // Déclencher la validation API après 500ms
      const timer = setTimeout(() => {
        validateSiretWithAPI(value);
      }, 500);

      setSiretValidationTimer(timer);
    }
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation des mots de passe
    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    // Validation du mot de passe selon les nouvelles règles
    const validation = validatePassword(formData.password);
    if (!validation.isValid) {
      setError(validation.errors[0] || 'Le mot de passe ne respecte pas les critères requis');
      return;
    }

    // Double validation de l'email
    if (!validateEmail(formData.email)) {
      setError('Veuillez fournir une adresse email valide');
      return;
    }

    // Vérifier que le SIRET est validé par le backend
    if (!siretValidated) {
      setError('Veuillez attendre la validation du SIRET avant de continuer');
      return;
    }

    // Validation du SIRET (obligatoire)
    const siretValidation = validateSiret(formData.siret);
    if (!siretValidation.isValid) {
      setError(siretValidation.error || 'Le SIRET fourni n\'est pas valide');
      return;
    }

    // Préparer les données pour l'API (sans confirmPassword)
    // Normaliser le SIRET avant l'envoi (supprimer espaces et tirets)
    const { confirmPassword, ...registerData } = formData;
    const dataToSend = {
      ...registerData,
      siret: normalizeSiret(formData.siret),
      isExemptFromTVA: siretValidationData?.isExemptFromTVA || false, // Inclure le statut TVA détecté
    };

    try {
      const success = await register(dataToSend);
      if (success) {
        // Rediriger vers la page de bienvenue pour présenter la période d'essai Premium
        navigate('/welcome');
      }
    } catch (err: any) {
      // Gestion des erreurs spécifiques du backend
      const errorMessage = err.message || 'Erreur lors de l\'inscription';

      // Messages d'erreur plus clairs pour les cas spécifiques
      if (errorMessage.includes('SIRET') || errorMessage.includes('siret')) {
        if (errorMessage.includes('période d\'essai') || errorMessage.includes('déjà')) {
          setError('Ce SIRET a déjà bénéficié d\'une période d\'essai. Veuillez contacter le support si vous pensez qu\'il s\'agit d\'une erreur.');
        } else if (errorMessage.includes('Luhn')) {
          setError('Le SIRET fourni n\'est pas valide. Veuillez vérifier le numéro.');
        } else if (errorMessage.includes('INSEE') || errorMessage.includes('Sirene')) {
          setError('Impossible de vérifier le SIRET auprès de l\'INSEE. Veuillez vérifier que le SIRET existe.');
        } else {
          setError(errorMessage);
        }
      } else if (errorMessage.toLowerCase().includes('email')) {
        setError('Cette adresse email est déjà utilisée');
      } else {
        setError(errorMessage);
      }
    }
  };

  // Nettoyer le timer au démontage du composant
  useEffect(() => {
    return () => {
      if (siretValidationTimer) {
        clearTimeout(siretValidationTimer);
      }
    };
  }, [siretValidationTimer]);

  // Déterminer si le bouton d'inscription doit être désactivé
  const isSubmitDisabled = isLoading || !siretValidated || !!emailError || !!siretError;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        padding: 2,
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={10}
          sx={{
            padding: 4,
            borderRadius: 3,
            background: 'white',
            border: `3px solid ${theme.palette.secondary.main}`,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Logo size={80} />
            </Box>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
              }}
            >
              Créer un compte
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Rejoignez Sersal - Gestion complète pour les métiers du bâtiment
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                icon={<VerifiedUser />}
                label="30 jours Premium offerts"
                color="success"
                size="small"
              />
              <Chip
                icon={<Info />}
                label="SIRET vérifié INSEE"
                variant="outlined"
                size="small"
              />
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Informations personnelles */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prénom"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  required
                  autoFocus
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nom"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  required
                />
              </Grid>

              {/* Email avec double validation */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  error={!!emailError}
                  helperText={emailError || 'Votre adresse email professionnelle'}
                  InputProps={{
                    endAdornment: formData.email && !emailError && (
                      <InputAdornment position="end">
                        <CheckCircle color="success" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Mots de passe */}
              <Grid item xs={12} sm={6}>
                <PasswordTextField
                  fullWidth
                  label="Mot de passe"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                {formData.password && (
                  <Box sx={{ mt: 1 }}>
                    <PasswordStrengthIndicator password={formData.password} showProgress={false} />
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <PasswordTextField
                  fullWidth
                  label="Confirmer le mot de passe"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  error={formData.confirmPassword !== '' && formData.password !== formData.confirmPassword}
                />
              </Grid>

              {/* Informations entreprise */}
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 1 }}>
                  <strong>Validation SIRET automatique :</strong> Dès que vous aurez saisi 14 chiffres,
                  nous vérifierons votre SIRET auprès de l'INSEE et préremplirons automatiquement
                  les informations de votre entreprise.
                </Alert>
              </Grid>

              {/* SIRET avec validation en temps réel */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="SIRET"
                  name="siret"
                  value={formData.siret}
                  onChange={handleChange}
                  required
                  error={!!siretError}
                  helperText={siretError || "14 chiffres (avec ou sans espaces)"}
                  inputProps={{
                    inputMode: 'numeric',
                    maxLength: 17, // Augmenté pour permettre les espaces (ex: "732 829 320 00074")
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {siretValidating && <CircularProgress size={20} />}
                        {!siretValidating && siretValidated && (
                          <CheckCircle color="success" />
                        )}
                        {!siretValidating && siretError && (
                          <ErrorIcon color="error" />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Affichage des informations de l'entreprise récupérées */}
              {siretValidated && siretValidationData && siretValidationData.denominationSociale && (
                <Grid item xs={12}>
                  <Alert severity="success" icon={<Business />}>
                    <strong>Société trouvée :</strong> {siretValidationData.denominationSociale} ✅
                  </Alert>
                </Grid>
              )}

              {/* Avertissement si période d'essai déjà utilisée */}
              {siretValidated && siretValidationData?.alreadyUsedTrial && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    {siretValidationData.trialWarning ||
                     'Ce SIRET a déjà bénéficié d\'une période d\'essai. Vous ne pourrez pas bénéficier d\'une nouvelle période d\'essai.'}
                  </Alert>
                </Grid>
              )}

              {/* Entreprise (prérempli si SIRET validé) */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Entreprise"
                  name="entreprise"
                  value={formData.entreprise}
                  onChange={handleChange}
                  required
                  InputProps={{
                    readOnly: siretValidated && !!siretValidationData?.denominationSociale,
                  }}
                />
              </Grid>

              {/* Corps de métier */}
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Corps de métier</InputLabel>
                  <Select
                    name="corpsMetier"
                    value={formData.corpsMetier}
                    onChange={handleSelectChange}
                    label="Corps de métier"
                  >
                    {Object.entries(CORPS_METIER_LABELS).map(([key, label]) => (
                      <MenuItem key={key} value={key}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Téléphone"
                  name="telephone"
                  type="tel"
                  value={formData.telephone}
                  onChange={handleChange}
                />
              </Grid>

              {/* Champs Factur-X */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 1, color: theme.palette.primary.main, fontWeight: 600 }}>
                  Informations légales (Factur-X)
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SIREN (optionnel)"
                  name="siren"
                  value={formData.siren}
                  onChange={handleChange}
                  helperText="9 chiffres (SIREN) ou 14 chiffres (SIRET)"
                  inputProps={{
                    inputMode: 'numeric',
                    maxLength: 14,
                  }}
                  InputProps={{
                    readOnly: siretValidated && !!siretValidationData?.siren,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Code Pays ISO"
                  name="codePaysIso"
                  value={formData.codePaysIso}
                  onChange={handleChange}
                  placeholder="FR"
                  inputProps={{
                    maxLength: 2,
                    style: { textTransform: 'uppercase' },
                  }}
                />
              </Grid>

              {/* Alerts TVA selon le statut */}
              {siretValidated && siretValidationData?.isExemptFromTVA && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    <strong>Franchise en base de TVA détectée</strong><br/>
                    Vous êtes entrepreneur individuel et probablement sous le régime de la franchise
                    en base de TVA. Le champ "Numéro de TVA intracommunautaire" a été laissé vide.<br/>
                    <small>Si vous êtes assujetti à la TVA, veuillez saisir votre numéro manuellement.</small>
                  </Alert>
                </Grid>
              )}

              {siretValidated && !siretValidationData?.isExemptFromTVA && siretValidationData?.numTVAIntracommunautaire && (
                <Grid item xs={12}>
                  <Alert severity="success">
                    <strong>Numéro de TVA calculé automatiquement</strong><br/>
                    Votre numéro de TVA intracommunautaire a été calculé : <strong>{siretValidationData.numTVAIntracommunautaire}</strong><br/>
                    <small>Vous pouvez le modifier si nécessaire.</small>
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="N° TVA Intracommunautaire (optionnel)"
                  name="numTvaIntracom"
                  value={formData.numTvaIntracom}
                  onChange={handleChange}
                  placeholder="FR12345678901"
                  disabled={siretValidated && siretValidationData?.isExemptFromTVA}
                  helperText={
                    siretValidated && siretValidationData?.isExemptFromTVA
                      ? "Exonéré de TVA (franchise en base)"
                      : "Ex: FR12345678901"
                  }
                  inputProps={{
                    maxLength: 15,
                    style: { textTransform: 'uppercase' },
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      backgroundColor: siretValidated && siretValidationData?.isExemptFromTVA ? '#f9fafb' : 'white',
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Adresse"
                  name="adresse"
                  value={formData.adresse}
                  onChange={handleChange}
                  multiline
                  rows={2}
                  InputProps={{
                    readOnly: siretValidated && !!siretValidationData?.adresse,
                  }}
                />
              </Grid>
            </Grid>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isSubmitDisabled}
              startIcon={<PersonAddOutlined />}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                background: isSubmitDisabled
                  ? undefined
                  : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                '&:hover': {
                  background: isSubmitDisabled
                    ? undefined
                    : 'linear-gradient(135deg, #5568d3 0%, #6a4293 100%)',
                },
              }}
            >
              {isLoading ? 'Inscription...' : !siretValidated ? 'Veuillez valider le SIRET' : 'S\'inscrire'}
            </Button>

            {!siretValidated && formData.siret && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Le bouton d'inscription sera activé une fois votre SIRET validé par l'INSEE.
              </Alert>
            )}
          </form>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Vous avez déjà un compte ?{' '}
              <Link
                component={RouterLink}
                to="/login"
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Se connecter
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default RegisterPage;
